import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Shield, UserPlus, UserMinus, AlertCircle, Users, DollarSign, BarChart, Bell, Loader2, Mail, Send, TrendingUp, Handshake, Eye, MousePointerClick, TrendingUp as TrendingIcon } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown } from "lucide-react";

interface Profile {
  id: string;
  username: string | null;
  email?: string;
}

interface UserRole {
  user_id: string;
  role: string;
}

const AdminManagement = () => {
  const navigate = useNavigate();
  const { toast: toastHook } = useToast();
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [userRoles, setUserRoles] = useState<UserRole[]>([]);
  
  // Bulk operations state
  const [operationLoading, setOperationLoading] = useState<string | null>(null);
  const [results, setResults] = useState<any>(null);
  const [updateUserIds, setUpdateUserIds] = useState("");
  const [updateData, setUpdateData] = useState("");
  const [minAmount, setMinAmount] = useState("1000");
  const [notifUserIds, setNotifUserIds] = useState("");
  const [notifTitle, setNotifTitle] = useState("");
  const [notifMessage, setNotifMessage] = useState("");
  
  // Email automation state
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [emailStats, setEmailStats] = useState<any>(null);
  const [emailLogs, setEmailLogs] = useState<any[]>([]);
  const [abandonedCarts, setAbandonedCarts] = useState<any[]>([]);
  const [isEmailLogsOpen, setIsEmailLogsOpen] = useState(false);
  const [isAbandonedCartsOpen, setIsAbandonedCartsOpen] = useState(true);
  const [emailFormData, setEmailFormData] = useState({
    name: "",
    subject: "",
    html_content: "",
    send_delay_hours: 24,
    is_active: true,
  });

  // Visitor tracking state
  const [visitorStats, setVisitorStats] = useState<any>(null);

  useEffect(() => {
    checkAdminAccess();
    loadEmailCampaigns();
    loadEmailStats();
    loadEmailLogs();
    loadAbandonedCarts();
    loadVisitorStats();
  }, []);

  const checkAdminAccess = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate("/auth");
      return;
    }

    // Check if current user is admin
    const { data: roleData, error: roleError } = await supabase
      .from("user_roles")
      .select("*")
      .eq("user_id", session.user.id)
      .eq("role", "admin")
      .single();

    if (roleError || !roleData) {
      toast.error("Access denied: Admin privileges required");
      navigate("/dashboard");
      return;
    }

    setIsAdmin(true);
    await loadUsersAndRoles();
  };

  const loadUsersAndRoles = async () => {
    try {
      // Load all profiles
      const { data: profilesData, error: profilesError } = await supabase
        .from("profiles")
        .select("id, username");

      if (profilesError) throw profilesError;

      // Just use profiles data (auth admin API not accessible from client)
      setProfiles(profilesData || []);

      // Load all user roles
      const { data: rolesData, error: rolesError } = await supabase
        .from("user_roles")
        .select("*");

      if (rolesError) throw rolesError;

      setUserRoles(rolesData || []);
    } catch (error: any) {
      console.error("Error loading data:", error);
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const hasRole = (userId: string, role: string) => {
    return userRoles.some(ur => ur.user_id === userId && ur.role === role);
  };

  const toggleAdminRole = async (userId: string) => {
    const isCurrentlyAdmin = hasRole(userId, "admin");

    try {
      if (isCurrentlyAdmin) {
        // Remove admin role
        const { error } = await supabase
          .from("user_roles")
          .delete()
          .eq("user_id", userId)
          .eq("role", "admin");

        if (error) throw error;
        toast.success("Admin role removed");
      } else {
        // Add admin role
        const { error } = await supabase
          .from("user_roles")
          .insert({ user_id: userId, role: "admin" });

        if (error) throw error;
        toast.success("Admin role granted");
      }

      // Reload roles
      await loadUsersAndRoles();
    } catch (error: any) {
      console.error("Error toggling role:", error);
      toast.error(error.message || "Failed to update role");
    }
  };

  // Bulk operations functions
  const executeBulkOperation = async (operation: string, data: any) => {
    setOperationLoading(operation);
    setResults(null);

    try {
      const { data: result, error } = await supabase.functions.invoke('admin-bulk-operations', {
        body: { operation, data }
      });

      if (error) throw error;

      setResults(result);
      toastHook({
        title: "Success",
        description: `${operation} completed successfully`,
      });
    } catch (error: any) {
      toastHook({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setOperationLoading(null);
    }
  };

  const handleBulkUpdateUsers = () => {
    try {
      const userIds = updateUserIds.split(',').map(id => id.trim());
      const updates = JSON.parse(updateData);
      executeBulkOperation('bulk_update_users', { user_ids: userIds, updates });
    } catch (error) {
      toastHook({
        title: "Invalid Input",
        description: "Please check your JSON format",
        variant: "destructive",
      });
    }
  };

  const handleCalculateCommissions = () => {
    executeBulkOperation('calculate_referral_commissions', { 
      min_purchase_amount: parseInt(minAmount) 
    });
  };

  const handleGenerateAnalytics = () => {
    executeBulkOperation('generate_analytics', {});
  };

  const handleBulkNotifications = () => {
    const userIds = notifUserIds.split(',').map(id => id.trim());
    executeBulkOperation('bulk_send_notifications', {
      user_ids: userIds,
      title: notifTitle,
      message: notifMessage,
      type: 'admin'
    });
  };

  // Email automation functions
  const loadEmailCampaigns = async () => {
    const { data, error } = await supabase
      .from("email_campaigns")
      .select("*")
      .eq("template_type", "abandoned_cart")
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error loading campaigns:", error);
    } else {
      setCampaigns(data || []);
    }
  };

  const loadEmailStats = async () => {
    const { data: abandoned } = await supabase
      .from("abandoned_checkouts")
      .select("id, recovery_email_sent, converted");

    if (abandoned) {
      setEmailStats({
        totalAbandoned: abandoned.length,
        emailsSent: abandoned.filter((a) => a.recovery_email_sent).length,
        converted: abandoned.filter((a) => a.converted).length,
        conversionRate: abandoned.filter((a) => a.recovery_email_sent).length > 0
          ? (abandoned.filter((a) => a.converted).length / abandoned.filter((a) => a.recovery_email_sent).length * 100).toFixed(1)
          : 0,
      });
    }
  };

  const loadEmailLogs = async () => {
    const { data, error } = await supabase
      .from("email_logs")
      .select("*")
      .order("sent_at", { ascending: false })
      .limit(50);

    if (error) {
      console.error("Error loading email logs:", error);
    } else {
      setEmailLogs(data || []);
    }
  };

  const loadAbandonedCarts = async () => {
    const { data, error } = await supabase
      .from("abandoned_checkouts")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(100);

    if (error) {
      console.error("Error loading abandoned carts:", error);
    } else {
      setAbandonedCarts(data || []);
    }
  };

  const handleCreateCampaign = async (e: React.FormEvent) => {
    e.preventDefault();

    const { error } = await supabase.from("email_campaigns").insert({
      ...emailFormData,
      template_type: "abandoned_cart",
    });

    if (error) {
      console.error("Error creating campaign:", error);
      toast.error("Failed to create campaign");
    } else {
      toast.success("Campaign created successfully!");
      setEmailFormData({
        name: "",
        subject: "",
        html_content: "",
        send_delay_hours: 24,
        is_active: true,
      });
      loadEmailCampaigns();
    }
  };

  const handleProcessAbandoned = async () => {
    toast.info("Processing abandoned carts...");
    
    const { data, error } = await supabase.functions.invoke("process-abandoned-carts");

    if (error) {
      console.error("Error processing abandoned carts:", error);
      toast.error("Failed to process abandoned carts");
    } else {
      toast.success(`Sent ${data.emailsSent} recovery emails!`);
      loadEmailStats();
      loadEmailLogs();
      loadAbandonedCarts();
    }
  };

  const loadVisitorStats = async () => {
    try {
      // Get total page views in the last 30 days
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const { data: pageViews, error } = await supabase
        .from("analytics_events")
        .select("*")
        .eq("event_type", "page_view")
        .gte("created_at", thirtyDaysAgo.toISOString());

      if (error) throw error;

      // Get unique visitors (by user_id and IP/userAgent combo)
      const uniqueVisitors = new Set();
      const pathCounts: { [key: string]: number } = {};
      const dailyViews: { [key: string]: number } = {};

      pageViews?.forEach((view: any) => {
        // Count unique visitors
        const identifier = view.user_id || `${view.metadata?.userAgent}-${view.metadata?.referrer}`;
        uniqueVisitors.add(identifier);

        // Count views per path
        const path = view.metadata?.path || 'unknown';
        pathCounts[path] = (pathCounts[path] || 0) + 1;

        // Count daily views
        const date = new Date(view.created_at).toLocaleDateString();
        dailyViews[date] = (dailyViews[date] || 0) + 1;
      });

      // Get most popular pages
      const topPages = Object.entries(pathCounts)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 5)
        .map(([path, count]) => ({ path, count }));

      // Get today's views
      const today = new Date().toLocaleDateString();
      const todayViews = dailyViews[today] || 0;

      setVisitorStats({
        totalViews: pageViews?.length || 0,
        uniqueVisitors: uniqueVisitors.size,
        todayViews,
        topPages,
        dailyViews: Object.entries(dailyViews).map(([date, count]) => ({ date, count })),
      });
    } catch (error) {
      console.error("Error loading visitor stats:", error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
      <div className="container mx-auto px-4 py-12 max-w-5xl">
        <Link to="/dashboard">
          <Button variant="ghost" className="mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </Link>

        <div className="space-y-8">
          <div className="text-center space-y-4 animate-fade-in">
            <div className="flex items-center justify-center gap-3">
              <Shield className="w-10 h-10 text-primary" />
              <h1 className="text-4xl font-bold">Admin Management</h1>
            </div>
            <p className="text-xl text-muted-foreground">
              Manage user roles and permissions
            </p>
          </div>

          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <strong>Important:</strong> To make your first admin, you'll need to manually insert a record into the user_roles table via your backend dashboard.
            </AlertDescription>
          </Alert>

          {/* Visitor Statistics Overview */}
          {visitorStats && (
            <div className="grid gap-4 md:grid-cols-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-sm font-medium">
                    <Eye className="h-4 w-4 text-primary" />
                    Total Page Views
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{visitorStats.totalViews.toLocaleString()}</div>
                  <p className="text-xs text-muted-foreground">Last 30 days</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-sm font-medium">
                    <Users className="h-4 w-4 text-primary" />
                    Unique Visitors
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{visitorStats.uniqueVisitors.toLocaleString()}</div>
                  <p className="text-xs text-muted-foreground">Last 30 days</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-sm font-medium">
                    <TrendingIcon className="h-4 w-4 text-primary" />
                    Today's Views
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{visitorStats.todayViews.toLocaleString()}</div>
                  <p className="text-xs text-muted-foreground">{new Date().toLocaleDateString()}</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-sm font-medium">
                    <MousePointerClick className="h-4 w-4 text-primary" />
                    Avg. Daily Views
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {Math.round(visitorStats.totalViews / 30).toLocaleString()}
                  </div>
                  <p className="text-xs text-muted-foreground">Per day</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Top Pages */}
          {visitorStats?.topPages && visitorStats.topPages.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart className="h-5 w-5" />
                  Most Popular Pages
                </CardTitle>
                <CardDescription>Top 5 pages by views in the last 30 days</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {visitorStats.topPages.map((page: any, index: number) => (
                    <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                      <div className="flex items-center gap-3">
                        <Badge variant="outline">{index + 1}</Badge>
                        <span className="font-mono text-sm">{page.path}</span>
                      </div>
                      <Badge variant="secondary">{page.count} views</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Quick Admin Links */}
          <div className="grid gap-4 md:grid-cols-4">
            <Link to="/admin/invites">
              <Card className="hover:border-primary/50 transition-colors cursor-pointer">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <UserPlus className="h-5 w-5" />
                    User Invitations
                  </CardTitle>
                  <CardDescription>Manually invite and manage user access</CardDescription>
                </CardHeader>
              </Card>
            </Link>
            <Link to="/admin/pricing">
              <Card className="hover:border-primary/50 transition-colors cursor-pointer">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <DollarSign className="h-5 w-5" />
                    Pricing Management
                  </CardTitle>
                  <CardDescription>Manage course pricing and settings</CardDescription>
                </CardHeader>
              </Card>
            </Link>
            <Link to="/admin/affiliates">
              <Card className="hover:border-primary/50 transition-colors cursor-pointer">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <TrendingUp className="h-5 w-5" />
                    Affiliate Management
                  </CardTitle>
                  <CardDescription>Manage all affiliates and transactions</CardDescription>
                </CardHeader>
              </Card>
            </Link>
            <Link to="/admin/partners">
              <Card className="hover:border-primary/50 transition-colors cursor-pointer">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Handshake className="h-5 w-5" />
                    Partner Management
                  </CardTitle>
                  <CardDescription>Manage partner offers and profiles</CardDescription>
                </CardHeader>
              </Card>
            </Link>
            <Link to="/admin/partner-inquiries">
              <Card className="hover:border-primary/50 transition-colors cursor-pointer">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Mail className="h-5 w-5" />
                    Partnership Inquiries
                  </CardTitle>
                  <CardDescription>View and manage partnership requests</CardDescription>
                </CardHeader>
              </Card>
            </Link>
            <Link to="/admin/email-templates">
              <Card className="hover:border-primary/50 transition-colors cursor-pointer">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Mail className="h-5 w-5" />
                    Email Templates
                  </CardTitle>
                  <CardDescription>Manage affiliate email templates</CardDescription>
                </CardHeader>
              </Card>
            </Link>
            <Link to="/admin/invoices">
              <Card className="hover:border-primary/50 transition-colors cursor-pointer">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <DollarSign className="h-5 w-5" />
                    Invoice Management
                  </CardTitle>
                  <CardDescription>Create and manage user invoices</CardDescription>
                </CardHeader>
              </Card>
            </Link>
            <Link to="/create-test-users">
              <Card className="hover:border-primary/50 transition-colors cursor-pointer">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Users className="h-5 w-5" />
                    Create Test Users
                  </CardTitle>
                  <CardDescription>Generate test accounts for testing</CardDescription>
                </CardHeader>
              </Card>
            </Link>
          </div>

          <Tabs defaultValue="roles" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="roles">User Roles</TabsTrigger>
              <TabsTrigger value="operations">Bulk Operations</TabsTrigger>
              <TabsTrigger value="emails">Email Automation</TabsTrigger>
            </TabsList>

            <TabsContent value="roles">
              <Card className="border-2">
                <CardHeader>
                  <CardTitle>User Roles</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {profiles.length === 0 ? (
                      <p className="text-center text-muted-foreground py-8">
                        No users found
                      </p>
                    ) : (
                      profiles.map((profile) => (
                        <div
                          key={profile.id}
                          className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                        >
                          <div className="flex-1">
                            <p className="font-medium">
                              {profile.username || "No username"}
                            </p>
                            {profile.email && (
                              <p className="text-sm text-muted-foreground">
                                {profile.email}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-3">
                            {hasRole(profile.id, "admin") ? (
                              <Badge variant="default" className="gap-1">
                                <Shield className="w-3 h-3" />
                                Admin
                              </Badge>
                            ) : (
                              <Badge variant="outline">User</Badge>
                            )}
                            <Button
                              variant={hasRole(profile.id, "admin") ? "destructive" : "default"}
                              size="sm"
                              onClick={() => toggleAdminRole(profile.id)}
                            >
                              {hasRole(profile.id, "admin") ? (
                                <>
                                  <UserMinus className="w-4 h-4 mr-2" />
                                  Remove Admin
                                </>
                              ) : (
                                <>
                                  <UserPlus className="w-4 h-4 mr-2" />
                                  Make Admin
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="operations" className="space-y-6">
              {/* Bulk Update Users */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Bulk Update Users
                  </CardTitle>
                  <CardDescription>Update multiple user profiles at once</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>User IDs (comma-separated)</Label>
                    <Input
                      placeholder="uuid1, uuid2, uuid3"
                      value={updateUserIds}
                      onChange={(e) => setUpdateUserIds(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Update Data (JSON)</Label>
                    <Textarea
                      placeholder='{"bio": "Updated bio", "username": "newname"}'
                      value={updateData}
                      onChange={(e) => setUpdateData(e.target.value)}
                      rows={4}
                    />
                  </div>
                  <Button
                    onClick={handleBulkUpdateUsers}
                    disabled={operationLoading === 'bulk_update_users'}
                  >
                    {operationLoading === 'bulk_update_users' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Update Users
                  </Button>
                </CardContent>
              </Card>

              {/* Calculate Referral Commissions */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="h-5 w-5" />
                    Calculate Referral Commissions
                  </CardTitle>
                  <CardDescription>Process pending referrals and calculate earnings</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Minimum Purchase Amount (cents)</Label>
                    <Input
                      type="number"
                      value={minAmount}
                      onChange={(e) => setMinAmount(e.target.value)}
                    />
                  </div>
                  <Button
                    onClick={handleCalculateCommissions}
                    disabled={operationLoading === 'calculate_referral_commissions'}
                  >
                    {operationLoading === 'calculate_referral_commissions' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Calculate Commissions
                  </Button>
                </CardContent>
              </Card>

              {/* Generate Analytics */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart className="h-5 w-5" />
                    Generate Analytics
                  </CardTitle>
                  <CardDescription>Aggregate platform statistics</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button
                    onClick={handleGenerateAnalytics}
                    disabled={operationLoading === 'generate_analytics'}
                  >
                    {operationLoading === 'generate_analytics' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Generate Analytics
                  </Button>
                </CardContent>
              </Card>

              {/* Bulk Send Notifications */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Bell className="h-5 w-5" />
                    Bulk Send Notifications
                  </CardTitle>
                  <CardDescription>Send notifications to multiple users</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>User IDs (comma-separated)</Label>
                    <Input
                      placeholder="uuid1, uuid2, uuid3"
                      value={notifUserIds}
                      onChange={(e) => setNotifUserIds(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Title</Label>
                    <Input
                      placeholder="Notification title"
                      value={notifTitle}
                      onChange={(e) => setNotifTitle(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Message</Label>
                    <Textarea
                      placeholder="Notification message"
                      value={notifMessage}
                      onChange={(e) => setNotifMessage(e.target.value)}
                      rows={3}
                    />
                  </div>
                  <Button
                    onClick={handleBulkNotifications}
                    disabled={operationLoading === 'bulk_send_notifications'}
                  >
                    {operationLoading === 'bulk_send_notifications' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Send Notifications
                  </Button>
                </CardContent>
              </Card>

              {/* Results Display */}
              {results && (
                <Card>
                  <CardHeader>
                    <CardTitle>Operation Results</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <pre className="bg-muted p-4 rounded-lg overflow-auto">
                      {JSON.stringify(results, null, 2)}
                    </pre>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="emails" className="space-y-6">
              {/* Stats Cards */}
              {emailStats && (
                <div className="grid gap-4 md:grid-cols-4">
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Total Abandoned</CardTitle>
                      <Users className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{emailStats.totalAbandoned}</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Emails Sent</CardTitle>
                      <Mail className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{emailStats.emailsSent}</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Recovered</CardTitle>
                      <TrendingUp className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{emailStats.converted}</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Conversion Rate</CardTitle>
                      <TrendingUp className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{emailStats.conversionRate}%</div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Abandoned Carts List */}
              <Collapsible open={isAbandonedCartsOpen} onOpenChange={setIsAbandonedCartsOpen}>
                <Card>
                  <CollapsibleTrigger asChild>
                    <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Users className="h-5 w-5" />
                          <div>
                            <CardTitle>Abandoned Checkouts</CardTitle>
                            <CardDescription>View all users who abandoned their checkout</CardDescription>
                          </div>
                        </div>
                        <ChevronDown className={`h-5 w-5 transition-transform ${isAbandonedCartsOpen ? 'rotate-180' : ''}`} />
                      </div>
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent>
                      {abandonedCarts.length === 0 ? (
                        <p className="text-center text-muted-foreground py-8">
                          No abandoned checkouts
                        </p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b">
                                <th className="text-left p-2">Email</th>
                                <th className="text-left p-2">Amount</th>
                                <th className="text-left p-2">Created</th>
                                <th className="text-left p-2">Email Sent</th>
                                <th className="text-left p-2">Converted</th>
                              </tr>
                            </thead>
                            <tbody>
                              {abandonedCarts.map((cart) => (
                                <tr key={cart.id} className="border-b hover:bg-muted/50">
                                  <td className="p-2 text-sm font-medium">{cart.email}</td>
                                  <td className="p-2 text-sm">${(cart.amount / 100).toFixed(2)}</td>
                                  <td className="p-2 text-sm text-muted-foreground">
                                    {new Date(cart.created_at).toLocaleString()}
                                  </td>
                                  <td className="p-2">
                                    <Badge variant={cart.recovery_email_sent ? 'default' : 'secondary'}>
                                      {cart.recovery_email_sent ? 'Yes' : 'No'}
                                    </Badge>
                                  </td>
                                  <td className="p-2">
                                    <Badge variant={cart.converted ? 'default' : 'secondary'}>
                                      {cart.converted ? 'Yes' : 'No'}
                                    </Badge>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>

              {/* Email Logs */}
              <Collapsible open={isEmailLogsOpen} onOpenChange={setIsEmailLogsOpen}>
                <Card>
                  <CollapsibleTrigger asChild>
                    <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Mail className="h-5 w-5" />
                          <div>
                            <CardTitle>Sent Emails History</CardTitle>
                            <CardDescription>Recent abandoned cart recovery emails</CardDescription>
                          </div>
                        </div>
                        <ChevronDown className={`h-5 w-5 transition-transform ${isEmailLogsOpen ? 'rotate-180' : ''}`} />
                      </div>
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent>
                      {emailLogs.length === 0 ? (
                        <p className="text-center text-muted-foreground py-8">
                          No emails sent yet
                        </p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b">
                                <th className="text-left p-2">Email</th>
                                <th className="text-left p-2">Subject</th>
                                <th className="text-left p-2">Status</th>
                                <th className="text-left p-2">Sent At</th>
                              </tr>
                            </thead>
                            <tbody>
                              {emailLogs.map((log) => (
                                <tr key={log.id} className="border-b hover:bg-muted/50">
                                  <td className="p-2 text-sm">{log.email}</td>
                                  <td className="p-2 text-sm">{log.subject}</td>
                                  <td className="p-2">
                                    <Badge variant={log.status === 'sent' ? 'default' : 'destructive'}>
                                      {log.status}
                                    </Badge>
                                  </td>
                                  <td className="p-2 text-sm text-muted-foreground">
                                    {new Date(log.sent_at).toLocaleString()}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>

              {/* Process Abandoned Carts */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Send className="h-5 w-5" />
                    Process Abandoned Carts
                  </CardTitle>
                  <CardDescription>Send recovery emails to users who abandoned checkout</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button onClick={handleProcessAbandoned}>
                    <Send className="w-4 h-4 mr-2" />
                    Process Now
                  </Button>
                </CardContent>
              </Card>

              {/* Campaign List */}
              <Card>
                <CardHeader>
                  <CardTitle>Active Campaigns</CardTitle>
                  <CardDescription>
                    Manage your abandoned cart recovery email campaigns
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {campaigns.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">
                      No campaigns yet. Create your first one below!
                    </p>
                  ) : (
                    <div className="space-y-4">
                      {campaigns.map((campaign) => (
                        <Card key={campaign.id}>
                          <CardHeader>
                            <div className="flex items-center justify-between">
                              <div>
                                <CardTitle className="text-lg">{campaign.name}</CardTitle>
                                <CardDescription>Subject: {campaign.subject}</CardDescription>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className={`px-2 py-1 rounded text-xs ${campaign.is_active ? 'bg-green-500/10 text-green-500' : 'bg-gray-500/10 text-gray-500'}`}>
                                  {campaign.is_active ? 'Active' : 'Inactive'}
                                </span>
                                <span className="text-sm text-muted-foreground">
                                  Delay: {campaign.send_delay_hours}h
                                </span>
                              </div>
                            </div>
                          </CardHeader>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Create Campaign Form */}
              <Card>
                <CardHeader>
                  <CardTitle>Create Abandoned Cart Campaign</CardTitle>
                  <CardDescription>
                    Set up an automated email to recover abandoned checkouts
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleCreateCampaign} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Campaign Name</Label>
                      <Input
                        id="name"
                        value={emailFormData.name}
                        onChange={(e) => setEmailFormData({ ...emailFormData, name: e.target.value })}
                        placeholder="e.g., 24h Abandoned Cart Recovery"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="subject">Email Subject</Label>
                      <Input
                        id="subject"
                        value={emailFormData.subject}
                        onChange={(e) => setEmailFormData({ ...emailFormData, subject: e.target.value })}
                        placeholder="e.g., Don't miss out on Launch To Revenue!"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="delay">Send Delay (hours)</Label>
                      <Input
                        id="delay"
                        type="number"
                        min="1"
                        value={emailFormData.send_delay_hours}
                        onChange={(e) => setEmailFormData({ ...emailFormData, send_delay_hours: parseInt(e.target.value) })}
                        required
                      />
                      <p className="text-sm text-muted-foreground">
                        How long to wait before sending the recovery email
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="html">Email HTML Content</Label>
                      <Textarea
                        id="html"
                        value={emailFormData.html_content}
                        onChange={(e) => setEmailFormData({ ...emailFormData, html_content: e.target.value })}
                        placeholder="<h1>Don't miss out!</h1><p>Complete your purchase for only {{price}}</p>"
                        rows={10}
                        required
                      />
                      <p className="text-sm text-muted-foreground">
                        Use {`{{price}}`} and {`{{email}}`} as placeholders
                      </p>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Switch
                        id="active"
                        checked={emailFormData.is_active}
                        onCheckedChange={(checked) => setEmailFormData({ ...emailFormData, is_active: checked })}
                      />
                      <Label htmlFor="active">Active</Label>
                    </div>

                    <Button type="submit" className="w-full">
                      Create Campaign
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default AdminManagement;
